/**
 * The API Client can be switched off like an extension (owner, 21.10.2026):
 * Settings → Extensions → the built-in "API Client" card
 * (components/settings/ApiClientExtensionCard.js). History is the API
 * Client's request history and goes with it — ONE switch, no History switch
 * of its own (owner, 28.10.2026).
 *
 * Off means nothing of it is reachable: no rail buttons, not listed under
 * Your tools, no API controls in the title bar, no API items in the command
 * palette, no "Open as request" in the terminal, no new request tab by
 * shortcut, and any switch to its pages (a shortcut, the assistant, a shared
 * link, a chat reference, a start-up on the last tab) is sent to the first
 * visible tool instead (`installApiClientGate`). An ATTEMPT to open it while
 * off says so ("The API Client is turned off") with a "Turn on" action.
 * Open request tabs are not closed: they are simply not shown (the request
 * editor lives on the API Client's pages) and are back, unsaved edits
 * included, when it is turned on. History recording stops: nothing is
 * written while it is off (hooks/collections/useRequestHistory addToHistory).
 *
 * Its DATA is untouched and still loaded — the Graph and the project sync
 * read the collections and workspaces — so turning it back on restores
 * everything.
 *
 * MCP (decided 28.10.2026): the collections tools keep answering while it is
 * off. Settings → MCP's own grants are the switch for what an agent may do,
 * and a built-in's on/off has never reached MCP (turning Boards or Documents
 * off does not refuse board_* / document_*; capabilities/enforce.js exempts
 * compiled-in code from the extension capability gate for the same reason).
 * Hiding a UI must not silently break an agent setup the user granted
 * separately; the card says so. Pinned by apiClientSwitch.test.js.
 *
 * The flag lives in the extension store (`apiClientEnabled`, persisted per
 * account with the rail settings, never synced); read it with
 * `selectApiClientEnabled` / `isApiClientEnabled` from ./store. This module is
 * the rule around it.
 */
import { RAIL_TOOL_IDS, withFollowers } from '../onboarding/railIds';

/** The rail tools and the page ids the switch turns off. */
export const API_CLIENT_TOOLS = Object.freeze(['collections', 'history']);
const API_CLIENT_PAGES = new Set(API_CLIENT_TOOLS);

export const isApiClientPage = (tab) => API_CLIENT_PAGES.has(String(tab || ''));

/** The extension a rail tool belongs to (`db-manager.activity` → `com.apiboo.db-manager`), or null for a built-in. */
export const extensionOfTool = (id) => (String(id).endsWith('.activity') ? `com.apiboo.${String(id).slice(0, -'.activity'.length)}` : null);

/** The page a rail tool opens (`task-manager.activity` → `task-manager.panel`; built-ins keep their id). */
export const pageOfTool = (id) => (String(id).endsWith('.activity') ? `${String(id).slice(0, -'.activity'.length)}.panel` : String(id));

/**
 * Where to go when an API Client page is asked for while it is off: the first
 * rail tool that is visible (not hidden, its extension enabled), else Settings'
 * own fallback, the Boards page. Pure.
 */
export function fallbackPage({ railHidden = [], enabled = {} } = {}) {
  const hidden = withFollowers(railHidden);
  const tool = RAIL_TOOL_IDS.find((id) => {
    if (API_CLIENT_PAGES.has(id) || hidden.has(id)) return false;
    const ext = extensionOfTool(id);
    return !ext || enabled[ext] !== false;
  });
  return tool ? pageOfTool(tool) : 'task-manager.panel';
}

/** The tools Your tools / the rail may show: without the API Client's while it is off. */
export const withoutApiClientTools = (ids, apiClientEnabled) =>
  apiClientEnabled === false ? (ids || []).filter((id) => !API_CLIENT_PAGES.has(id)) : ids || [];

/**
 * Sends the app away from an API Client page while the switch is off — now,
 * and on every later attempt. `ui` is the uiModalStore, `ext` the extension
 * store (both zustand). `onBlocked(tab)` runs for a later ATTEMPT only (a page
 * switch to `tab` while off) — not for the start-up check and not for the
 * moment the switch itself is turned off, which are not something the user
 * asked to open. Returns the unsubscribe.
 */
export function installApiClientGate(ui, ext, { onBlocked } = {}) {
  // Both must be real zustand stores; a test double of either gets no gate.
  const real = (st) => st && typeof st.getState === 'function' && typeof st.subscribe === 'function';
  if (!real(ui) || !real(ext)) return () => {};
  const check = (attempt) => {
    const e = ext.getState();
    if (e.apiClientEnabled !== false) return;
    const tab = ui.getState().activeTab;
    if (!isApiClientPage(tab)) return;
    ui.getState().setActiveTab(fallbackPage({ railHidden: e.railHidden, enabled: e.enabled }));
    if (attempt && typeof onBlocked === 'function') onBlocked(tab);
  };
  check(false);
  const offUi = ui.subscribe((s, prev) => {
    if (s.activeTab !== prev.activeTab) check(true);
  });
  const offExt = ext.subscribe((s, prev) => {
    if (s.apiClientEnabled !== prev.apiClientEnabled) check(false);
  });
  return () => {
    offUi();
    offExt();
  };
}
